import { Box, Typography, TextField } from "@mui/material";
import { useMemo, useEffect } from "react";

const FIELDS = [
  { key: "james_suckling", label: "James Suckling" },
  { key: "robert_parker", label: "Robert Parker" },
  { key: "jeb_dunnuck", label: "Jeb Dunnuck" },
  { key: "tim_atkin", label: "Tim Atkin" },
  { key: "wine_spectator", label: "Wine Spectator" },
  { key: "wine_enthusiast", label: "Wine Enthusiast" },
  { key: "wine_and_spirits", label: "Wine & Spirits" },
  { key: "vinous", label: "Vinous" },
  { key: "allen_meadows_burghound", label: "Allen Meadows Burghound" },
  { key: "whisky_advocate", label: "Whisky Advocate" },
  { key: "decanter", label: "Decanter" },
]

// showNotes: shows a description field under each rating (custom.rating_notes, products only).
export default function ExpertRatingsSection({
  values = {},
  setValues = () => {},
  showNotes = false,
}) {

  const handleNoteChange = (key, value) => {
    setValues((prev) => ({
      ...prev,
      metafields: {
        ...(prev.metafields || {}),
        rating_notes: { ...(prev.metafields?.rating_notes || {}), [key]: value },
      },
    }));
  };

  const handleFieldChange = (key, value) => {
    setValues((prev) => ({
      ...prev,
      metafields: {
        ...(prev.metafields || {}),
        [key]: value,
      },
    }));
  };

  const handleBlur = (key, value) => {
    if (value === "" || isNaN(value)) return;

    const formatted = parseFloat(value).toFixed(2);

    setValues((prev) => ({
      ...prev,
      metafields: {
        ...prev.metafields,
        [key]: formatted,
      },
    }));
  };

  // Filter rating values from metafields
  const ratingValues = useMemo(() => FIELDS.map((f) => values.metafields?.[f.key]), [values.metafields]);

  useEffect(() => {
    const ratings = ratingValues
      .map((n) => parseFloat(n))
      .filter((n) => !isNaN(n));

    const best = ratings.length ? Math.max(...ratings).toFixed(2) : "";

    if (values.metafields?.best_rate !== best) {
      setValues((prev) => ({
        ...prev,
        metafields: { ...(prev.metafields || {}), best_rate: best },
      }));
    }
  }, [ratingValues, values.metafields?.best_rate, setValues]);

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Expert Ratings
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: showNotes ? "repeat(3, 1fr)" : "repeat(4, 1fr)",
          columnGap: showNotes ? 4 : 2,
          rowGap: showNotes ? 4 : 2,
        }}
      >
        {FIELDS.map((f) => (
          <Box key={f.key} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label={f.label}
              value={values.metafields?.[f.key] || ""}
              type="number"
              fullWidth
              size="small"
              slotProps={{ htmlInput: { step: 0.5 } }}
              onChange={(e) => handleFieldChange(f.key, e.target.value)}
              onBlur={(e) => handleBlur(f.key, e.target.value)}
            />
            {showNotes && (
              <TextField
                label="Description"
                slotProps={{ htmlInput: { "aria-label": `${f.label} description` } }}
                value={values.metafields?.rating_notes?.[f.key] || ""}
                fullWidth
                size="small"
                multiline
                minRows={3}
                onChange={(e) => handleNoteChange(f.key, e.target.value)}
              />
            )}
          </Box>
        ))}

        <TextField
          label="Best Rate"
          value={values.metafields?.best_rate || ""}
          fullWidth
          size="small"
          type="number"
          disabled
          slotProps={{ htmlInput: { step: 0.5 } }}
        />
      </Box>
    </Box>
  );
}